import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  applyBaselineAsParameters,
  getDetectionRangeBySupplierName,
  getGeneralConfigurationParameters,
  getGeneralConfigurationParametersFromDevice,
  getOperationModes,
  SendCommandToGetAssetConfiguration,
  setAssetGeneralConfiguration,
  setBaselineConfigurations,
} from "src/core/api/services/command-module/general-config.service";
import ModalComponent from "src/core/components/modal/modal.component";
import SkeletonComponent from "src/shared/custom-spinner/skeleton.component";
import { safeAtob } from "src/shared/helpers/enode-decode-string";
import { displayToast } from "src/shared/helpers/toaster.helper";
import {
  sanitizeNumericInput,
  LIGHTING_FIELD_CONFIG,
  SENSOR_THRESHOLD_CONFIG,
} from "./hooks/use-general-configuration-validation.hook";
import {
  useGeneralConfigurationValidation,
} from "./hooks/use-general-configuration-validation.hook";
import { useCountdown } from "../components/hooks/use-countdown.hook";
import "../asset-configuration.scss";
import { useSocketAck } from "../components/hooks/use-socket-ack.hook";
import ConfigurationHistoryTableComponent from "./config-history-table.component";
import { ConfigurationSliderCard } from "./components/configuration-cards.component";
import { ConfigurationToolbar } from "./components/configuration-toolbar.component";
import { GatewayConfigModal } from "./components/gateway-config-modal.component";
import type {
  AsyncStatus,
  GatewayConfigData,
  LightingValues,
  SensorThresholdField,
  SensorValues,
} from "../components/types";
import { LightingConfigurationSection } from "./components/lighting-configuration.component";
import { SensorConfigurationSection } from "./components/sensor-configuration.component";

const GeneralConfigurationComponent = () => {
  const params: any = useParams();
  const assetCode = params.id ? safeAtob(params.id) : null;

  const [isAdvancedConfigModalOpen, setIsAdvancedConfigModalOpen] = useState<boolean>(false);
  const [isGatewayConfigAndRestoreModalOpen, setIsGatewayConfigAndRestoreModalOpen] = useState<boolean>(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState<boolean>(false);
  const [generalConfigParamsFromDeviceData, setGeneralConfigParamsFromDeviceData] = useState<GatewayConfigData | null>(null);
  const [isLoadingCommandResultData, setIsLoadingCommandResultData] = useState<boolean>(false);
  const [isLoadingRestoreData, setIsLoadingRestoreData] = useState<boolean>(false);
  const [gatewayConfigFetchStatus, setGatewayConfigFetchStatus] = useState<"idle" | "pending" | "success" | "failed">("idle");
  const [applyingChangesACKStatus, setApplyingChangesACKStatus] = useState<"idle" | "pending" | "success" | "failed">("idle");

  // Countdown for "Get Gateway Config" — active while status is "pending"
  const { countdown: gatewayConfigCountdown } = useCountdown({
    totalSeconds: 300,
    active: gatewayConfigFetchStatus === "pending",
    onExpire: () => setGatewayConfigFetchStatus("failed"),
  });

  // Countdown for "Apply Changes" — active while status is "pending"
  const { countdown: applyChangesCountdown } = useCountdown({
    totalSeconds: 300,
    active: applyingChangesACKStatus === "pending",
    onExpire: () => setApplyingChangesACKStatus("failed"),
  });

  const [isBackingUpGatewayConfig, setIsBackingUpGatewayConfig] = useState<boolean>(false);
  const [gatewayConfigBackupError, setGatewayConfigBackupError] = useState<string | null>(null);
  const [isRestoreMode, setIsRestoreMode] = useState<boolean>(false);
  const [isRestoringGatewayConfig, setIsRestoringGatewayConfig] = useState<boolean>(false);
  const [restoreConfigError, setRestoreConfigError] = useState<string | null>(null);
  const [operationMode, setOperationMode] = useState<string | null>(null);
  const gatewayConfigTimeoutRef = useRef<number | null>(null);
  const [hasMissingBaselineValues, setHasMissingBaselineValues] = useState<boolean>(false);
  const [hasMissingCurrentValues, setHasMissingCurrentValues] = useState<boolean>(false);
  const [failsafeDelay, setFailsafeDelay] = useState<number | null>(null);
  const [detectionRangeOptions, setDetectionRangeOptions] = useState<{ id: number, name: string }[]>([]);
  const [lightingValues, setLightingValues] = useState<LightingValues>({
    rampUpTime: null,
    rampDownTime: null,
    dimLevel: null,
    brightLevel: null,
    holdTime: null,
  });

  const [sensorValues, setSensorValues] = useState<SensorValues>({
    detectionRangeId: null,
    lowerThreshold: null,
    upperThreshold: null,
    isPhotoSensing: null
  });

  const validation = useGeneralConfigurationValidation(lightingValues, sensorValues);
  const { displayedErrors, touchField, validateForm, setFieldError } = validation;

  const assetDetails = useSelector((state: any) => {
    return state.asset.assetDetails;
  });

  const clearGatewayConfigTimer = () => {
    if (gatewayConfigTimeoutRef.current) {
      clearTimeout(gatewayConfigTimeoutRef.current);
      gatewayConfigTimeoutRef.current = null;
    }
  };

  const startGatewayConfigTimer = (timerFor: "gateway_config_command" | "apply_changes") => {
    clearGatewayConfigTimer();

    gatewayConfigTimeoutRef.current = window.setTimeout(() => {
      if (timerFor == "apply_changes") {
        setApplyingChangesACKStatus("failed");
      } else {
        setGatewayConfigFetchStatus("failed");
      }
      gatewayConfigTimeoutRef.current = null;
    }, 5 * 60 * 1000);
  };

  /*   const socketRealTimeUpdate = useSelector((state: any) => {
      return state.asset.socketRealTimeUpdate;
    }); */

  const {
    data: operationModesData,
  } = useQuery({
    queryKey: ["getOperationModes"],
    queryFn: ({ signal }) => getOperationModes(signal),
  });

  const {
    data: detectionRangesData,
  } = useQuery({
    queryKey: ["getDetectionRangeBySupplierName", assetDetails?.vendor],
    queryFn: ({ signal }) => getDetectionRangeBySupplierName(assetDetails?.vendor, signal),
    enabled: !!assetDetails?.vendor,
  });

  useEffect(() => {
    if (detectionRangesData?.data?.length > 0) {
      setDetectionRangeOptions(detectionRangesData?.data)
    }
  }, [detectionRangesData])

  const operationModeOptions = useMemo(() => operationModesData?.data || [], [operationModesData]);
  /*   const detectionRangeOptions = useMemo(() => {
      if (!assetDetails) return [];
  
      if (assetDetails?.vendor == "iSense" || assetDetails?.vendor == "Esave") {
        return levels.slice(0, 3);
      } else if (assetDetails?.vendor == "Pelucchi") {
        return levels.slice(0, 9);
      } else {
        return levels;
      }
  
    }, [assetDetails]); */

  const {
    data: generalConfigurationParameters,
    isLoading: isLoadingGeneralConfigurationParameters,
    refetch: refetchGeneralConfigParamaters
  } = useQuery({
    queryKey: [
      "getGeneralConfigurationParameters",
      assetCode
    ],
    queryFn: ({ signal }) =>
      getGeneralConfigurationParameters(assetCode, signal),
  });

  useEffect(() => {
    const generalConfigData = generalConfigurationParameters?.data;

    setLightingValues({
      rampUpTime: generalConfigData?.rampUpTime,
      rampDownTime: generalConfigData?.rampDownTime,
      dimLevel: generalConfigData?.dimLevel,
      brightLevel: generalConfigData?.brightLevel,
      holdTime: generalConfigData?.timeDelay,
    })

    setSensorValues({
      detectionRangeId: generalConfigData?.detectionRangeId,
      lowerThreshold: generalConfigData?.photoLowerThreshold,
      upperThreshold: generalConfigData?.photoUpperThreshold,
      isPhotoSensing: generalConfigData?.photoSensing
    })

    const matchedOperationMode = operationModeOptions.find(
      (mode: any) => mode.id === generalConfigData?.operationModeId,
    );

    setOperationMode(matchedOperationMode?.name || generalConfigData?.operationModeId || "null");
    setFailsafeDelay(generalConfigData?.failsafeDelay);
  }, [generalConfigurationParameters, operationModeOptions])

  useEffect(() => {
    if (gatewayConfigFetchStatus === "success" || gatewayConfigFetchStatus === "failed") {
      const timer = setTimeout(() => {
        setGatewayConfigFetchStatus("idle");
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [gatewayConfigFetchStatus]);

  useEffect(() => {
    if (applyingChangesACKStatus === "success" || applyingChangesACKStatus === "failed") {
      const timer = setTimeout(() => {
        setApplyingChangesACKStatus("idle");
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [applyingChangesACKStatus]);

  useEffect(() => {
    return () => {
      clearGatewayConfigTimer();
    };
  }, []);

  const onSubmitAdvancedConfigModal = () => {
    if (failsafeDelay === null || failsafeDelay < 0 || failsafeDelay > 120) {
      setFieldError("failsafeDelay", "Value must be between 0 and 120");
      return;
    }
    setIsAdvancedConfigModalOpen(false);
  }

  const handleApplyChanges = async () => {
    if (!assetCode) return;

    const isValid = validateForm();
    if (!isValid) return;

    setApplyingChangesACKStatus("pending")
    startGatewayConfigTimer("apply_changes");

    try {
      const selectedOperationMode = operationModeOptions.find(
        (mode: any) => mode.name === operationMode,
      );

      const operationModeId = selectedOperationMode?.id === undefined ? null : selectedOperationMode?.id;
      const response = await setAssetGeneralConfiguration({
        assetCode,
        operationModeId: operationModeId,
        failsafeDelay: failsafeDelay ?? 0,
        rampUpTime: lightingValues.rampUpTime,
        rampDownTime: lightingValues.rampDownTime,
        dimLevel: lightingValues.dimLevel,
        brightLevel: lightingValues.brightLevel,
        timeDelay: lightingValues.holdTime,
        detectionRangeId: sensorValues.detectionRangeId,
        photoLowerThreshold: sensorValues.lowerThreshold,
        photoUpperThreshold: sensorValues.upperThreshold,
        photoSensing: (sensorValues.isPhotoSensing),
      });

      if (response?.status !== 201) {
        setApplyingChangesACKStatus("idle");
        clearGatewayConfigTimer();
      }
    } catch (error: any) {
      console.error("setAssetGeneralConfiguration error: ", error)

      clearGatewayConfigTimer();
      setApplyingChangesACKStatus("failed");
    }
  };

  const handleCloseModal = () => {
    setIsAdvancedConfigModalOpen(false);
  };

  const handleOpenHistoryModal = () => {
    setIsHistoryModalOpen(true);
  };

  const handleCloseHistoryModal = () => {
    setIsHistoryModalOpen(false);
  };

  const handleCloseGatewayConfigModal = () => {
    setIsGatewayConfigAndRestoreModalOpen(false);
    setIsRestoreMode(false);
    setRestoreConfigError(null);
  };

  const sendCommandToGetGatewayConfig = async () => {
    if (!assetCode) return;

    setGatewayConfigFetchStatus("pending");
    startGatewayConfigTimer("gateway_config_command");

    try {
      await SendCommandToGetAssetConfiguration(assetCode, undefined);
    } catch (error: any) {
      console.error("sendCommandToGetGatewayConfig error: ", error)
      clearGatewayConfigTimer();
      setGatewayConfigFetchStatus("failed");
    }
  };

  const handleViewGatewayConfigFromDevice = async (requestFor: 'restore' | 'command_result') => {
    if (!assetCode) return;

    setGeneralConfigParamsFromDeviceData(null);
    if (requestFor == 'command_result') {

      setIsLoadingCommandResultData(true);
    } else {
      setIsLoadingRestoreData(true);
    }

    try {
      const response = await getGeneralConfigurationParametersFromDevice(
        assetCode,
        undefined,
      );

      setIsGatewayConfigAndRestoreModalOpen(true);
      setIsLoadingCommandResultData(false);
      setIsLoadingRestoreData(false);

      setGeneralConfigParamsFromDeviceData(response?.data ?? null);
    } catch (error: any) {
      console.error("Failed to get general config from device: ", error);
      setIsLoadingCommandResultData(false);
      setIsLoadingRestoreData(false);
    }
  };

  const handleOpenRestoreModal = async () => {
    await handleViewGatewayConfigFromDevice("restore");
    setIsRestoreMode(true);
    setIsGatewayConfigAndRestoreModalOpen(true);
  };

  const handleBackupGatewayConfig = async () => {
    if (!assetCode) return;
    setIsBackingUpGatewayConfig(true);
    setGatewayConfigBackupError(null);

    try {
      const configurationItems = (generalConfigParamsFromDeviceData?.parameters ?? []).map(
        (parameter) => ({
          configurationParameterId: parameter.parameterId,
          value: String(parameter.currentValue ?? ""),
        }),
      );

      await setBaselineConfigurations(assetCode, configurationItems);
      setIsGatewayConfigAndRestoreModalOpen(false);
      // await sendCommandToGetGatewayConfig();
    } catch (error: any) {
      setGatewayConfigBackupError(
        error?.response?.data?.message ||
        error?.message ||
        "Failed to backup configuration.",
      );
    } finally {
      setIsBackingUpGatewayConfig(false);
      setIsGatewayConfigAndRestoreModalOpen(false);
    }
  };

  const handleRestoreGatewayConfig = async () => {
    if (!assetCode) return;
    setIsRestoringGatewayConfig(true);
    setRestoreConfigError(null);

    try {
      const configurationItems = (generalConfigParamsFromDeviceData?.parameters ?? []).map(
        (parameter) => ({
          configurationParameterId: parameter?.parameterId,
          value: String(parameter?.baselineValue),
        }),
      );

      await applyBaselineAsParameters(assetCode, configurationItems);
      // await sendCommandToGetGatewayConfig();
    } catch (error: any) {
      setRestoreConfigError(
        error?.response?.data?.message ||
        error?.message ||
        "Failed to restore configuration.",
      );
    } finally {
      setIsRestoringGatewayConfig(false);
      setIsGatewayConfigAndRestoreModalOpen(false);
    }
  };

  const handleLightingChange = (
    field: keyof LightingValues,
    rawValue: any,
  ) => {
    const config = LIGHTING_FIELD_CONFIG[field];
    const sanitized = sanitizeNumericInput(rawValue, config.min, config.max);

    setLightingValues((current) => ({ ...current, [field]: sanitized }));
  };

  const handleLightingReset = (field: keyof LightingValues) => {
    setLightingValues((current) => ({ ...current, [field]: 0 }));
  };

  const handleThresholdChange = (
    field: SensorThresholdField,
    rawValue: any,
  ) => {
    const config = SENSOR_THRESHOLD_CONFIG[field];
    const sanitized = sanitizeNumericInput(rawValue, config.min, config.max);

    setSensorValues((current) => ({ ...current, [field]: sanitized }));
  };

  const handleThresholdReset = (field: SensorThresholdField) => {
    setSensorValues((current) => ({ ...current, [field]: 0 }));
  };

  const handleDetectionRangeChange = (value: number) => {
    setSensorValues((current) => ({ ...current, detectionRangeId: value }));
  };

  const handleEnableDetectionRange = () => {
    setSensorValues((current) => ({
      ...current,
      detectionRangeId: detectionRangeOptions[0]?.id || 1,
    }));
  };

  const handleTogglePhotoSensing = (checked: boolean) => {
    setSensorValues((current) => ({ ...current, isPhotoSensing: checked }));
  };

  const modalActionConfig: any = [
    {
      label: "Save",
      event: onSubmitAdvancedConfigModal,
      variant: "primary",
    },
  ];


  useEffect(() => {
    const parameters = generalConfigParamsFromDeviceData?.parameters ?? [];

    const isMissingBaselineValues = parameters.some(
      ({ baselineValue }) => baselineValue == null || baselineValue === ""
    );

    const isMissingCurrentValues = parameters.some(
      ({ currentValue }) => currentValue == null || currentValue === ""
    );

    setHasMissingBaselineValues(isMissingBaselineValues);
    setHasMissingCurrentValues(isMissingCurrentValues);
  }, [generalConfigParamsFromDeviceData]);


  const gatewayConfigActionConfig: any = [
    {
      label: isBackingUpGatewayConfig ? "Backing Up..." : "Backup",
      event: handleBackupGatewayConfig,
      variant: "primary",
      disabled: isBackingUpGatewayConfig || hasMissingCurrentValues,
    },
  ];

  const restoreConfigActionConfig: any = [
    {
      label: isRestoringGatewayConfig ? "Restoring..." : "Restore",
      event: handleRestoreGatewayConfig,
      variant: "primary",
      disabled: isRestoringGatewayConfig || hasMissingBaselineValues,
    },
  ];

  useSocketAck({
    ackKey: "GeneralConfiguration",
    onAck: () => {
      clearGatewayConfigTimer();
      setApplyingChangesACKStatus("success");
      refetchGeneralConfigParamaters();
    },
    assetCode: assetCode,
  });

  useSocketAck({
    ackKey: "GetGatewayConfigFromDevice",
    onAck: () => {
      setGatewayConfigFetchStatus("success");
      clearGatewayConfigTimer();
    },
    assetCode: assetCode,
  });

  const lastSyncDisplay = generalConfigParamsFromDeviceData?.lastSync
    ? new Date(generalConfigParamsFromDeviceData.lastSync).toLocaleString()
    : "Not available";

  return (
    <div className="configuration-section">
      <ConfigurationToolbar
        applyingChangesACKStatus={applyingChangesACKStatus}
        applyChangesCountdown={applyChangesCountdown}
        onViewHistory={handleOpenHistoryModal}
        onRestore={handleOpenRestoreModal}
        onApplyChanges={handleApplyChanges}
      />

      <div className="configuration-dashboard">
        <LightingConfigurationSection
          operationModeOptions={operationModeOptions}
          operationMode={operationMode}
          onOperationModeChange={setOperationMode}
          lightingValues={lightingValues}
          onLightingChange={handleLightingChange}
          onLightingReset={handleLightingReset}
          gatewayConfigFetchStatus={gatewayConfigFetchStatus}
          onGetGatewayConfig={sendCommandToGetGatewayConfig}
          isLoadingCommandResultData={isLoadingCommandResultData}
          onViewCommandResult={() => handleViewGatewayConfigFromDevice("command_result")}
          onOpenAdvancedConfig={() => setIsAdvancedConfigModalOpen(true)}
          errors={displayedErrors}
          gatewayConfigCountdown={gatewayConfigCountdown}
          isUpdatedByZone={generalConfigurationParameters?.data?.isUpdatedByZone}
          updateStatusMessage={generalConfigurationParameters?.data?.updateStatusMessage}
        />

        <SensorConfigurationSection
          detectionRangeOptions={detectionRangeOptions}
          sensorValues={sensorValues}
          onEnableDetectionRange={handleEnableDetectionRange}
          onDetectionRangeChange={handleDetectionRangeChange}
          onTogglePhotoSensing={handleTogglePhotoSensing}
          onThresholdChange={handleThresholdChange}
          onThresholdReset={handleThresholdReset}
          errors={displayedErrors}
        />

        {/* Modals */}
        {isAdvancedConfigModalOpen && (
          <ModalComponent
            showModal={isAdvancedConfigModalOpen}
            handleClose={handleCloseModal}
            headerTitle={"Advanced Settings"}
            actionConfig={modalActionConfig}
          >

            <SkeletonComponent
              wrapperHeight="280px"
              skeletonHeight="50px"
              isLoading={isLoadingGeneralConfigurationParameters}
              count={2}
            >
              <ConfigurationSliderCard
                title="FailSafe Delay"
                suffix="min"
                value={failsafeDelay}
                min={0}
                max={120}
                step={1}
                onValueChange={(rawValue) => {
                  const sanitized = sanitizeNumericInput(rawValue, 0, 120);
                  touchField("failsafeDelay");
                  setFailsafeDelay(sanitized);
                }}
                onClick={() => setFailsafeDelay(0)}
                error={displayedErrors.failsafeDelay}
              />
            </SkeletonComponent>
          </ModalComponent>
        )}

        {isHistoryModalOpen && (
          <ConfigurationHistoryTableComponent
            assetCode={assetCode}
            isOpen={isHistoryModalOpen}
            handleClose={handleCloseHistoryModal}
            applyingChangesACKStatus={applyingChangesACKStatus}
          />
        )}

        <GatewayConfigModal
          showModal={isGatewayConfigAndRestoreModalOpen}
          handleClose={handleCloseGatewayConfigModal}
          isRestoreMode={isRestoreMode}
          isLoading={isLoadingCommandResultData || isLoadingRestoreData}
          data={generalConfigParamsFromDeviceData}
          errorMessage={isRestoreMode ? restoreConfigError : gatewayConfigBackupError}
          actionConfig={isRestoreMode ? restoreConfigActionConfig : gatewayConfigActionConfig}
          lastSyncDisplay={lastSyncDisplay}
        />
      </div>
    </div>
  );
}

export default GeneralConfigurationComponent;